<template>
  <div class="e4s-flex-row" v-if="isTrack">
    <div
      class="e4s-flex-column e4s-justify-flex-end e4s-header--500 e4s-flex-row--end"
      v-text="getTrackWind"
    ></div>
  </div>

  <div
    v-for="individualResult in resultsInternal"
    :key="individualResult.athleteId + '-' + individualResult.score"
  >
    <RaceResult
      :id="'athlete-result-' + individualResult.athleteId"
      :individual-result="individualResult"
      :api-competition-athlete="storeCompResultsState.athletes[individualResult.athleteId]"
      :heat-no="'heatNo' in individualResult ? individualResult.heatNo! : heatNo"
      :is-track="isTrack"
      :is-team-event="isTeamEvent"
      :individual-entry="getIndividualEntryForAthlete(individualResult.athleteId)"
      :athlete-trial-map="eventTrialMap[individualResult.athleteId]"
    />
    <!--    ============= ch: {{ individualResult.hasScoreChanged }} ani:-->
    <!--    {{ individualResult.animateNewScore }}-->
  </div>
</template>

<script setup lang="ts">
import type {
  AthleteTrialMap,
  IndividualEntry,
  IndividualResult,
  StoreCompResultsState
} from '@/components/results/store-comp-results'
import { computed, type PropType, ref, watch } from 'vue'

import {
  athleteReorderDetails,
  type IndividualResultWithHeat,
  sortRaceResultsByPosition
} from '@/components/results/results-service'
import type { AthleteIdString } from '@/services/common'
import RaceResult from '@/components/results/race-results/RaceResult.vue'
import {
  simpleClone,
  uniqueObjects,
  uniqueResultsByAthleteId
} from '@/services/common-service-utils'

export interface IndividualResultAnimated extends IndividualResult, IndividualResultWithHeat {
  hasScoreChanged: boolean
  hasChangedPosition: boolean
  hasPositionImproved: boolean
  animateNewScore: boolean
}

const props = defineProps({
  storeCompResultsState: {
    type: Object as PropType<StoreCompResultsState>,
    required: true
  },
  individualResults: {
    type: Array as PropType<IndividualResult[] | IndividualResultWithHeat[]>,
    required: true
  },
  entries: {
    type: Object as PropType<Record<AthleteIdString, IndividualEntry>>,
    required: false
  },
  isTrack: {
    type: Boolean,
    required: false
  },
  isTeamEvent: {
    type: Boolean,
    required: true
  },
  heatNo: {
    type: [Number, String],
    required: true
  },
  eventTrialMap: {
    type: Object as PropType<Record<AthleteIdString, AthleteTrialMap>>,
    default: () => {
      return {}
    }
  },
  sortingRequired: {
    type: Boolean,
    required: true
  }
})

// const lastResults = ref<IndividualResultAnimated[]>([])
const resultsInternal = ref<IndividualResultAnimated[]>([])

// map the incoming individualResults to IndividualResultAnimated and set the internal results
const individualResultsAnimated = mapIndividualResultsAnimated(simpleClone(props.individualResults))
setInternalResults(individualResultsAnimated)

function mapIndividualResultsAnimated(
  individualResults: (IndividualResult | IndividualResultWithHeat)[]
): IndividualResultAnimated[] {
  /**
   * getting instance like:
   * [{
   *    "laneNo": 0,
   *    "position": 7,
   *    "wind": "",
   *    "athlete": "Juliette MACQUIN-JONES",
   *    "athleteId": 194382,
   *    "affilation": "North Yorkshire",
   *    "bibNo": "61",
   *    "score": "2.85",
   *    "qualify": null,
   *    "hasScoreChanged": false,
   *    "hasChangedPosition": false,
   *    "hasPositionImproved": false,
   *    "animateNewScore": false
   *  }, {
   *    "laneNo": 0,
   *    "position": 7,
   *    "wind": "",
   *    "athlete": "Juliette MACQUIN-JONES",
   *    "athleteId": 194382,
   *    "affilation": "North Yorkshire",
   *    "bibNo": "61",
   *    "score": "2.85",
   *    "qualify": null,
   *    "hasScoreChanged": false,
   *    "hasChangedPosition": false,
   *    "hasPositionImproved": false,
   *    "animateNewScore": false
   *  },{
   *    "laneNo": 0,
   *    "position": 1,
   *    "wind": "",
   *    "athlete": "Ella BROOKER",
   *    "athleteId": 183978,
   *    "affilation": "Kent",
   *    "bibNo": "41",
   *    "score": "2.85",
   *    "qualify": null,
   *    "hasScoreChanged": false,
   *    "hasChangedPosition": false,
   *    "hasPositionImproved": false,
   *    "animateNewScore": false
   *  }]
   *  where we have duplicate objects.  These need uniqueing.
   *
   */
  // const uniqueResults = uniqueObjects(individualResults)
  const uniqueResults = uniqueResultsByAthleteId(individualResults)

  return uniqueResults.map((result) => {
    return {
      ...result,
      hasScoreChanged: false,
      hasChangedPosition: false,
      hasPositionImproved: false,
      animateNewScore: false
    } as IndividualResultAnimated
  })
}

watch(
  () => props.individualResults,
  (newValue, oldValue) => {
    // console.error('individualResults changed', newValue, oldValue)
    animateChange(newValue, oldValue)
  }
)

function animateChange(
  newValue: (IndividualResult | IndividualResultWithHeat)[],
  oldValue: (IndividualResult | IndividualResultWithHeat)[]
): void {
  const animateThisAthlete = newValue.find((result, index) => {
    return result.animateThisAthlete
  })
  if (!animateThisAthlete) {
    setInternalResults(mapIndividualResultsAnimated(simpleClone(newValue)))
    return
  }

  const athleteId = animateThisAthlete.athleteId
  const reorderDetails = athleteReorderDetails(oldValue, newValue, athleteId)

  // map oldValue and find the athleteId of the first athlete with animateThisAthlete set to true
  const currentValues = simpleClone(resultsInternal.value).map((result) => {
    if (result.athleteId === athleteId) {
      return {
        ...result,
        animateThisAthlete: false,
        hasScoreChanged: reorderDetails.hasScoreChanged,
        hasPositionImproved: reorderDetails.newPosition < reorderDetails.originalPosition,
        animateNewScore: false
      }
    }
    return result
  })

  // Sort the results by position
  if (props.sortingRequired) {
    currentValues.sort((a, b) => {
      return a.position - b.position
    })
  }

  resultsInternal.value = currentValues
  setTimeout(() => {
    const newValues: IndividualResultAnimated[] = simpleClone(newValue).map((result) => {
      const resultAnimated: IndividualResultAnimated = {
        ...result,
        animateThisAthlete: false,
        hasScoreChanged: false,
        hasPositionImproved: false,
        hasChangedPosition: false,
        animateNewScore: false
      }
      if (result.athleteId === athleteId) {
        resultAnimated.hasScoreChanged = false
        resultAnimated.animateNewScore = true
      }
      return resultAnimated
    })

    setInternalResults(newValues)
  }, 2000)
}

function setInternalResults(newValue: IndividualResultAnimated[]): void {
  // lastResults.value = simpleClone(oldValue)
  if (props.sortingRequired) {
    resultsInternal.value = sortRaceResultsByPosition(
      simpleClone(newValue)
    ) as IndividualResultAnimated[]
  } else {
    resultsInternal.value = simpleClone(newValue)
  }
}

function getIndividualEntryForAthlete(athleteId: number | string): IndividualEntry | undefined {
  if (!props.entries) {
    return undefined
  }
  return props.entries[athleteId.toString()]
}

const getTrackWind = computed(() => {
  const result: IndividualResult = props.individualResults[0]

  if (!result) {
    return 'Wind: N/A'
  }
  // get wind
  const wind = result.wind
  if (!wind) {
    return 'Wind: N/A'
  }

  console.log('RaceResults.getTrackWind', wind)

  const windStr = wind.toString()
  const hasMs = windStr.toLowerCase().includes('m/s')
  return 'Wind: ' + (hasMs ? windStr : windStr + ' m/s')
})
</script>

<style scoped>
.race-result--position {
  width: 50px;
}

.pulse-change-green {
  animation-duration: 2s;
  animation-name: pulse-change-green;
}

.pulse-change-yellow {
  animation-duration: 2s;
  animation-name: pulse-change-yellow;
}

.pulse-change-red {
  animation-duration: 2s;
  animation-name: pulse-change-red;
}
</style>
